import { FC } from 'react';
import { useDesign, useServices } from '../../../hooks';
import {
  BreakingChangeType,
  Design,
  DesignFeatureFlag,
  ObjectClass,
  Option
} from '../../../types';
import {
  addBreakingChange,
  loopObjectsAndNodes,
  normalizeObjectOrNode
} from '../../../utilities';
import { DesignRootFeatureFlag } from './design-root-feature-flag';

const designFeatureFlagOptions: Option<DesignFeatureFlag>[] = Object.entries({
  [DesignFeatureFlag.inputNumberVariables]: { label: 'Allow variables for numbers on Inputs' },
  [DesignFeatureFlag.intentManagement]: { label: 'Intent Management' },
  [DesignFeatureFlag.ttsOverrides]: { label: 'Multiple TTS Voices per Locale' }
} satisfies Record<DesignFeatureFlag, { label: string; }>).map(([value, option]) => {
  return {
    ...option,
    value: value as DesignFeatureFlag
  };
});

export const DesignRootFeatureFlags: FC = () => {
  const serialized = useDesign();
  const { intentService } = useServices();
  const { features } = serialized.rootSettings;

  return (
    <div className="row">
      <div className="col-md-12">
        <label>Feature Flags</label>
        <div className="box content equalChildren">
          {/* OmniChannel is old and was created before @see designFeatureFlagOptions so it must be handled separately. */}
          <DesignRootFeatureFlag
            enabled={serialized.omniChannel.isOmniChannel}
            label={window.l.settings.omniChannel}
            onEnable={async () => {
              enableOmniChannel(serialized);
            }}
            tooltip="Enables the use of conversation variables and multiple session channels"
          />
          {designFeatureFlagOptions.map(({ label, value: flagId }) => {
            const enabled = !!features[flagId];
            return (
              <DesignRootFeatureFlag
                enabled={enabled}
                key={flagId}
                label={label}
                onEnable={async () => {
                  features[flagId] = true;
                  const { timestamp } = window.apm;
                  serialized.rootSettings.lastLocalChange = timestamp;
                  switch (flagId) {
                    case DesignFeatureFlag.inputNumberVariables: {
                      loopObjectsAndNodes({
                        callback: ({ appModule, item }) => {
                          if (item._class === ObjectClass.input) {
                            normalizeObjectOrNode(item, serialized, appModule ?? undefined);
                            item.props.lastLocalChange = timestamp;
                          }
                        },
                        serialized
                      });
                      return;
                    }
                    case DesignFeatureFlag.intentManagement: {
                      return await intentService.hydrateDesign(serialized);
                    }
                    case DesignFeatureFlag.ttsOverrides:
                      // This makes new UI elements show up, so nothing new is needed.
                      return;
                    default: {
                      const neverFlag: never = flagId;
                      throw new Error(`DesignFeatureFlag '${neverFlag}' failed to enable.`);
                    }
                  }
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

function enableOmniChannel(serialized: Design) {
  window.apm.knowledgeBase.addScopeToAppVariables(serialized.components.variables);
  window.apm.knowledgeBase.udgradePromptConfigForSimultaneous(serialized);

  serialized.omniChannel = {
    isOmniChannel: true,
    lastLocalChange: window.apm.timestamp
  };

  addBreakingChange(serialized, {
    message: 'Omni Channel enabled.',
    timestamp: window.apm.timestamp,
    type: BreakingChangeType.omniChannelEnabled
  });

  // TODO: only refresh tabs whose flows contain prompts
  window.apm.postEventToAllViews({ target: 'request-reload-data-model' });
}
